// Smart search functionality for both tools and resources
// This provides more precise search results than simple string matching

import { Tool } from '@/hooks/useTools';
import { Resource } from '@/services/resourceService';

// Enhanced keyword mappings for semantic search
const TOOL_CATEGORY_KEYWORDS = {
  'Design': ['design', 'ui', 'ux', 'interface', 'visual', 'graphic', 'layout', 'aesthetic', 'styling', 'appearance', 'brand', 'identity', 'creative', 'art', 'composition', 'typography', 'color', 'themes'],
  'Productivity': ['productivity', 'note', 'notes', 'note taking', 'note-taking', 'notebook', 'notepad', 'journal', 'journaling', 'diary', 'memo', 'memos', 'task', 'tasks', 'todo', 'to-do', 'planning', 'organization', 'organize'],
  'Prototyping': ['prototype', 'wireframe', 'mockup', 'interactive', 'demo', 'preview', 'sketch', 'draft', 'concept', 'proof of concept', 'mvp', 'rapid', 'iteration'],
  'Research': ['research', 'testing', 'user test', 'analytics', 'feedback', 'survey', 'interview', 'analysis', 'insights', 'data', 'metrics', 'study', 'usability'],
  'Collaboration': ['collaboration', 'team', 'share', 'comment', 'review', 'workflow', 'teamwork', 'group work', 'cooperative', 'together', 'communication', 'remote work'],
  'Motion': ['motion', 'animation', 'animate', 'movement', 'transition', 'micro-interaction', 'lottie', 'gif'],
  'Development': ['development', 'code', 'coding', 'programming', 'developer', 'build', 'frontend', 'backend', 'api', 'web development'],
  'No-code': ['no-code', 'nocode', 'low-code', 'visual programming', 'drag and drop', 'builder', 'visual builder'],
};

const TOOL_USE_CASE_KEYWORDS = {
  'UI design': ['ui', 'user interface', 'interface design', 'screen design', 'app design', 'web design', 'mobile design'],
  'Design systems': ['design system', 'component library', 'style guide', 'brand guidelines', 'design tokens', 'components'],
  'Wireframing': ['wireframe', 'wireframing', 'low-fi', 'structure', 'layout planning', 'sitemap'],
  'Prototyping': ['prototype', 'prototyping', 'interactive', 'clickable', 'demo', 'proof of concept'],
  'User testing': ['user testing', 'usability testing', 'user research', 'feedback', 'testing', 'validation'],
  'Analytics': ['analytics', 'metrics', 'data', 'insights', 'tracking', 'measurement', 'statistics'],
  'Note taking': ['note', 'notes', 'note taking', 'note-taking', 'notebook', 'notepad', 'journal', 'writing', 'documentation'],
  'Animation': ['animation', 'motion', 'micro-interactions', 'transitions', 'lottie', 'after effects'],
};

const RESOURCE_CONTENT_KEYWORDS = {
  'Article': ['article', 'blog', 'post', 'guide', 'tutorial', 'how-to', 'tips', 'best practices'],
  'Guide': ['guide', 'handbook', 'manual', 'reference', 'documentation', 'tutorial', 'step-by-step'],
  'Template': ['template', 'starter', 'boilerplate', 'framework', 'kit', 'mockup', 'wireframe template'],
  'Tool': ['tool', 'software', 'app', 'platform', 'service', 'utility', 'resource'],
  'Course': ['course', 'training', 'lesson', 'education', 'learning', 'class', 'workshop'],
  'Book': ['book', 'ebook', 'pdf', 'publication', 'reading', 'literature'],
  'Video': ['video', 'tutorial', 'screencast', 'webinar', 'presentation', 'demo'],
};

const RESOURCE_TOPIC_KEYWORDS = {
  'UX Design': ['ux', 'user experience', 'usability', 'user research', 'persona', 'user journey', 'user flow'],
  'UI Design': ['ui', 'user interface', 'visual design', 'interface design', 'screen design'],
  'Design Systems': ['design system', 'component library', 'style guide', 'design tokens', 'pattern library'],
  'Accessibility': ['accessibility', 'a11y', 'inclusive design', 'wcag', 'screen reader', 'accessible'],
  'Prototyping': ['prototype', 'prototyping', 'wireframe', 'mockup', 'interactive design'],
  'Research': ['research', 'user research', 'usability testing', 'interviews', 'surveys', 'analytics'],
};

// Helper function to safely get string value
function safeString(value: any): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

// Helper function to safely get array value
function safeArray(value: any): string[] {
  if (Array.isArray(value)) return value.filter(item => typeof item === 'string');
  return [];
}

// Calculate relevance score for a tool
export function calculateToolRelevance(tool: Tool, query: string): number {
  const searchTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 1);
  if (searchTerms.length === 0) return 0;

  let totalScore = 0;
  let maxPossibleScore = 0;

  for (const term of searchTerms) {
    let termScore = 0;
    let termMaxScore = 100; // Maximum possible score per term

    // Direct name match (highest priority)
    const toolName = safeString(tool.name).toLowerCase();
    if (toolName.includes(term)) {
      if (toolName === term) {
        termScore += 50; // Exact match
      } else if (toolName.startsWith(term)) {
        termScore += 40; // Starts with
      } else {
        termScore += 30; // Contains
      }
    }

    // Summary match
    const toolSummary = safeString(tool.summary).toLowerCase();
    if (toolSummary.includes(term)) {
      termScore += 25;
    }

    // Category semantic matching
    for (const [category, keywords] of Object.entries(TOOL_CATEGORY_KEYWORDS)) {
      if (keywords.some(keyword => keyword.includes(term) || term.includes(keyword))) {
        if (safeArray(tool.category).includes(category)) {
          termScore += 20;
        }
      }
    }

    // Use case semantic matching
    for (const [useCase, keywords] of Object.entries(TOOL_USE_CASE_KEYWORDS)) {
      if (keywords.some(keyword => keyword.includes(term) || term.includes(keyword))) {
        if (safeArray(tool.use_cases).includes(useCase)) {
          termScore += 15;
        }
      }
    }

    // Direct category/use case match
    if (safeArray(tool.category).some(cat => safeString(cat).toLowerCase().includes(term))) {
      termScore += 15;
    }
    if (safeArray(tool.use_cases).some(uc => safeString(uc).toLowerCase().includes(term))) {
      termScore += 15;
    }

    // Tags match
    if (safeArray(tool.tags).some(tag => safeString(tag).toLowerCase().includes(term))) {
      termScore += 10;
    }

    // Platform match
    if (safeArray(tool.platforms).some(platform => safeString(platform).toLowerCase().includes(term))) {
      termScore += 8;
    }

    // Pricing match
    const toolPricing = safeString(tool.pricing).toLowerCase();
    if (toolPricing.includes(term)) {
      termScore += 5;
    }

    totalScore += Math.min(termScore, termMaxScore);
    maxPossibleScore += termMaxScore;
  }

  // Calculate percentage score
  const percentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
  return Math.min(percentage, 100);
}

// Calculate relevance score for a resource
export function calculateResourceRelevance(resource: Resource, query: string): number {
  const searchTerms = query.toLowerCase().split(/\s+/).filter(term => term.length > 1);
  if (searchTerms.length === 0) return 0;

  let totalScore = 0;
  let maxPossibleScore = 0;

  for (const term of searchTerms) {
    let termScore = 0;
    let termMaxScore = 100; // Maximum possible score per term

    // Direct name match (highest priority)
    const resourceName = safeString(resource["Resource Name"]).toLowerCase();
    if (resourceName.includes(term)) {
      if (resourceName === term) {
        termScore += 50; // Exact match
      } else if (resourceName.startsWith(term)) {
        termScore += 40; // Starts with
      } else {
        termScore += 30; // Contains
      }
    }

    // Description match
    const resourceDescription = safeString(resource["Description"]).toLowerCase();
    if (resourceDescription.includes(term)) {
      termScore += 25;
    }

    // Content type semantic matching
    for (const [contentType, keywords] of Object.entries(RESOURCE_CONTENT_KEYWORDS)) {
      if (keywords.some(keyword => keyword.includes(term) || term.includes(keyword))) {
        const resourceContentType = safeString(resource["Content Type"]).toLowerCase();
        if (resourceContentType.includes(contentType.toLowerCase())) {
          termScore += 20;
        }
      }
    }

    // Topic semantic matching
    for (const [topic, keywords] of Object.entries(RESOURCE_TOPIC_KEYWORDS)) {
      if (keywords.some(keyword => keyword.includes(term) || term.includes(keyword))) {
        const topicTags = safeString(resource["Topic Tags"]).toLowerCase();
        if (topicTags.includes(topic.toLowerCase())) {
          termScore += 15;
        }
      }
    }

    // Direct content type match
    const resourceContentType = safeString(resource["Content Type"]).toLowerCase();
    if (resourceContentType.includes(term)) {
      termScore += 15;
    }

    // Topic tags match
    const topicTags = safeString(resource["Topic Tags"]).toLowerCase();
    if (topicTags.includes(term)) {
      termScore += 15;
    }

    // Use case match
    const useCase = safeString(resource["Recommended Use Case"]).toLowerCase();
    if (useCase.includes(term)) {
      termScore += 10;
    }

    totalScore += Math.min(termScore, termMaxScore);
    maxPossibleScore += termMaxScore;
  }

  // Calculate percentage score
  const percentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;
  return Math.min(percentage, 100);
}

// Smart search for tools with relevance-based filtering
export function smartSearchTools(tools: Tool[], query: string, minRelevance = 30): Tool[] {
  if (!query.trim()) return tools;

  const scoredTools = tools.map(tool => ({
    tool,
    relevance: calculateToolRelevance(tool, query.trim())
  }));

  return scoredTools
    .filter(item => item.relevance >= minRelevance)
    .sort((a, b) => b.relevance - a.relevance)
    .map(item => item.tool);
}

// Smart search for resources with relevance-based filtering
export function smartSearchResources(resources: Resource[], query: string, minRelevance = 30): Resource[] {
  if (!query.trim()) return resources;

  const scoredResources = resources.map(resource => ({
    resource,
    relevance: calculateResourceRelevance(resource, query.trim())
  }));

  return scoredResources
    .filter(item => item.relevance >= minRelevance)
    .sort((a, b) => b.relevance - a.relevance)
    .map(item => item.resource);
}

// Get search suggestions based on common queries
export function getToolSearchSuggestions(): string[] {
  return [
    'note taking tools',
    'design systems',
    'prototyping tools',
    'user testing platforms',
    'collaboration tools',
    'animation tools',
    'wireframing tools',
    'free design tools',
    'AI design tools',
    'mac design tools'
  ];
}

export function getResourceSearchSuggestions(): string[] {
  return [
    'accessibility guidelines',
    'design system examples',
    'UX research methods',
    'UI design patterns',
    'prototyping guides',
    'user testing templates',
    'design inspiration',
    'mobile design guides',
    'color theory guides',
    'typography guides'
  ];
}